import { effect, Injectable, signal } from '@angular/core';
import { PlatformService } from './platform.service';

interface INotification {
  message?: string;
  type: 'success' | 'error' | 'warning' | 'info';
  textColor?:
    | 'text-successContrast'
    | 'text-errorContrast'
    | 'text-warningContrast'
    | 'text-infoContrast';
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  notification = signal<INotification>({ message: undefined, type: 'success' });
  fadingOut = signal<boolean>(false);

  animationTime = 200; // 200 ms fade in and fade out
  timeoutTime = 3000; // 3 seconds before fading out

  timeout: any | undefined;

  constructor(private platform: PlatformService) {
    if (this.platform.isBrowser()) {
      effect(() => {
        if (this.notification().message) {
          this.fadingOut.set(false);
          if (this.timeout) {
            clearTimeout(this.timeout);
          }
          this.timeout = setTimeout(() => {
            this.close();
          }, this.timeoutTime);
        }
      });
    }
  }

  success(message?: string) {
    if (this.platform.isBrowser()) {
      this.notification.set({
        message: message ?? 'Success!',
        type: 'success',
        textColor: 'text-successContrast',
      });
    }
  }

  error(message?: string) {
    if (this.platform.isBrowser()) {
      this.notification.set({
        message: message ?? 'Something went wrong!',
        type: 'error',
        textColor: 'text-errorContrast',
      });
    }
  }

  warning(message: string) {
    if (this.platform.isBrowser()) {
      this.notification.set({
        message,
        type: 'warning',
        textColor: 'text-warningContrast',
      });
    }
  }

  info(message: string) {
    if (this.platform.isBrowser()) {
      this.notification.set({
        message,
        type: 'info',
        textColor: 'text-infoContrast',
      });
    }
  }

  close() {
    if (this.platform.isBrowser()) {
      this.fadingOut.set(true);
      setTimeout(() => {
        this.reset();
        if (this.timeout) {
          clearTimeout(this.timeout);
        }
      }, this.animationTime);
    }
  }

  reset() {
    if (this.platform.isBrowser()) {
      this.notification.set({ message: undefined, type: 'success' });
    }
  }
}
